<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import {
  mdiClose,
  mdiChevronLeft,
  mdiChevronRight,
  mdiThemeLightDark,
  mdiChat,
  mdiWalletOutline,
  mdiPlus,
  mdiSchoolOutline
} from '@mdi/js'
import { useRoute, useRouter } from 'vue-router'
import { tutorialLocationFor } from '@/components/tutorial/tutorial-steps'
import { useCrispChat } from '@/composables/useCrispChat'
import { useTokenBalance } from '@/composables/useTokenBalance'
import { tabbablesIn } from '@/composables/usePopover'
import { DIVIDER } from '@/menu.js'
import AsideMenuList from '@/components/AsideMenuList.vue'
import NavBarItem from '@/components/NavBarItem.vue'
import Icon from '@/components/Icon.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import JbLogo from '@/components/JbLogo.vue'

defineProps({
  menu: {
    type: Array,
    default: () => []
  }
})

const store = useStore()

const isFullScreen = computed(() => store.state.isFullScreen)
const isAsideMobileExpanded = computed(() => store.state.isAsideMobileExpanded)
const isAsideLgActive = computed(() => store.state.isAsideLgActive)
const isCollapsed = computed(() => store.state.isAsideCollapsed)

// The icon-only rail is a *docked* treatment. Both controls that widen it again
// are `hidden xl:flex`, so a collapsed sidebar opened as an overlay below xl
// would be a 4rem strip of unlabelled icons with no way to expand it. Whenever
// the sidebar is on screen as an overlay it shows its full 15rem self instead.
const isRail = computed(
  () => isCollapsed.value && !isAsideMobileExpanded.value && !isAsideLgActive.value
)
const darkMode = computed(() => store.state.darkMode)
const userName = computed(() => store?.state?.auth?.user?.user?.name)
const userEmail = computed(() => store?.state?.auth?.user?.user?.email)
const isLoggedIn = computed(() => store.state.auth.status.loggedIn)

/* ── Off-canvas drawer: keyboard ──────────────────────────────────────────
   Below xl the closed drawer is only moved off screen (`--aside-offset`) — it
   stays `display: grid` below lg — so its ~11 controls stayed in the Tab order
   and a keyboard user walked through a menu they could not see. While closed
   off-canvas it is now `inert` (out of the Tab order and the accessibility
   tree). From xl it is docked and always interactive, per
   docs/app-shell-responsive.md; the same query drives App.vue's body padding. */
const xlQuery = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(min-width: 1280px)')
  : null
const isXl = ref(xlQuery ? xlQuery.matches : true)
const onXlChange = (event) => { isXl.value = event.matches }

const isDrawerOpen = computed(() => isAsideMobileExpanded.value || isAsideLgActive.value)
const isOffCanvasClosed = computed(() => !isXl.value && !isDrawerOpen.value)

const asideRef = ref(null)

/* The drawer is opened from a hamburger in the top bar. Focus moves into it so
   a keyboard user lands on what they opened, and goes back to that hamburger
   when it is closed with Escape or its own close button — not when a link is
   followed, which is a navigation, or on a click on the backdrop. */
let drawerOpener = null

watch(isDrawerOpen, async (open) => {
  if (!open || isXl.value) return
  const active = document.activeElement
  drawerOpener = active && active !== document.body ? active : null
  // After the render that lifts `inert` (and, between lg and xl, `hidden`).
  await nextTick()
  const first = tabbablesIn(asideRef.value)[0]
  if (first) first.focus({ preventScroll: true })
})

const returnFocusToOpener = () => {
  const target = drawerOpener
  drawerOpener = null
  if (target && target.isConnected && target.getClientRects().length > 0) {
    target.focus({ preventScroll: true })
  }
}

// How far the sidebar sits off its docked edge. Fed to the `.aside-inline`
// helper as a custom property so it can be applied with `inset-inline-start`
// and mirror automatically under RTL (Arabic).
// NOTE: -4rem / -15rem must stay in sync with the body padding App.vue sets.
const asideOffset = computed(() => {
  if (isAsideMobileExpanded.value) return '0px'
  return isCollapsed.value ? '-4rem' : '-15rem'
})

/* Same rule as the top bar's balance pill (composables/useTokenBalance.js):
   neutral while funded; amber plus the words "Top up" when low. */
const {
  full: formattedTokens,
  compact: shortTokens,
  isLow: isLowBalance
} = useTokenBalance()

const tokensLabel = computed(() =>
  `Tokens: ${formattedTokens.value}${isLowBalance.value ? ' — top up' : ''}`
)

// One close control for every width, so it has to clear both flags: below lg the
// drawer is open via `isAsideMobileExpanded`, between lg and xl via
// `isAsideLgActive`. Dispatching only the lg one left the button inert on phones.
const asideClose = () => {
  store.dispatch('asideLgToggle', false)
  store.dispatch('asideMobileToggle', false)
}

const onCloseButton = () => {
  asideClose()
  returnFocusToOpener()
}

/* Escape closes the drawer the way it closes every other overlay. An open
   dialog on top (the chat confirmation from "Help", say) takes Escape first:
   ModalBox listens on window in the capture phase and stops it there. */
const onDocumentKeydown = (event) => {
  if (event.key !== 'Escape' || !isDrawerOpen.value) return
  asideClose()
  returnFocusToOpener()
}

/* Tabbing out of the open drawer (either end) closes it — the same contract as
   the header popovers (composables/usePopover.js). The page behind the
   backdrop stays focusable, and without this a keyboard user tabbed on into
   controls they could not see. Focus leaving #app altogether does not count:
   that is a dialog the drawer itself opened (Help's chat confirmation), which
   hands focus back here when it closes. */
const onDocumentFocusin = (event) => {
  if (!isDrawerOpen.value || isXl.value) return
  const target = event.target
  const app = document.getElementById('app')
  if (!(target instanceof Node) || !app || !app.contains(target)) return
  if (asideRef.value && asideRef.value.contains(target)) return
  asideClose()
}

onMounted(() => {
  document.addEventListener('keydown', onDocumentKeydown)
  document.addEventListener('focusin', onDocumentFocusin)
  if (xlQuery) {
    if (xlQuery.addEventListener) xlQuery.addEventListener('change', onXlChange)
    else if (xlQuery.addListener) xlQuery.addListener(onXlChange)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onDocumentKeydown)
  document.removeEventListener('focusin', onDocumentFocusin)
  if (xlQuery) {
    if (xlQuery.removeEventListener) xlQuery.removeEventListener('change', onXlChange)
    else if (xlQuery.removeListener) xlQuery.removeListener(onXlChange)
  }
})

const toggleCollapse = () => {
  store.dispatch('asideCollapseToggle')
}

const toggleDarkMode = () => {
  store.dispatch('darkMode')
}

const { openCrispChat } = useCrispChat()
const route = useRoute()
const router = useRouter()

// Following a link on mobile should get the drawer out of the way.
const menuClick = () => {
  if (isAsideMobileExpanded.value) {
    store.dispatch('asideMobileToggle', false)
  }
}

/* Three utility controls, not four. Docs and Log out both already sit in the
   account dropdown, which is where people look for them; a second copy here was
   costing permanent sidebar height to save nobody a click. Dark mode keeps its
   duplicate in the top bar deliberately — it is a control rather than a
   destination, and people hunt for it in both places. */
const utilities = computed(() => [
  {
    key: 'theme',
    icon: mdiThemeLightDark,
    label: darkMode.value ? 'Light' : 'Dark',
    title: darkMode.value ? 'Switch to light mode' : 'Switch to dark mode',
    action: toggleDarkMode
  },
  {
    /* The tutorial, opened at the step for the page you are on — the first
       place to look before asking a person (tutorial-steps.js). */
    key: 'guide',
    icon: mdiSchoolOutline,
    label: 'Guide',
    title: 'How does this page work? Open the tutorial',
    action: () => {
      router.push(tutorialLocationFor(route.path))
      menuClick()
    }
  },
  {
    key: 'help',
    icon: mdiChat,
    label: 'Help',
    title: 'Need help? Open chat',
    action: () => openCrispChat('Hi, I need some help.')
  }
])
</script>

<template>
  <!-- `transition-all` is deliberate here and must stay. This element animates
       its WIDTH (w-16 <-> w-60) and its inline-start offset, neither of which is
       in Tailwind's curated `transition` set - that covers colour, opacity,
       shadow and transform only. Swapping it makes the sidebar snap open. -->
  <aside
    v-show="!isFullScreen"
    id="app-sidebar"
    ref="asideRef"
    class="aside-inline fixed top-0 z-40 transition-all duration-300
           bg-white border-r border-gray-200
           dark:bg-gray-800 dark:border-gray-700"
    :style="{
      '--aside-offset': asideOffset,
      gridTemplateRows: 'auto minmax(0, 1fr) minmax(0, auto)',
      overflow: 'hidden'
    }"
    :class="[
      isRail ? 'w-16' : 'w-60',
      /* At xl the sidebar is docked and App.vue reserves body padding of
         exactly 4rem/15rem from `isAsideCollapsed` alone. Pin the docked width
         to that same source of truth, so a drawer left open across a resize
         back up to xl can never desync the two. */
      isCollapsed ? 'xl:w-16' : 'xl:w-60',
      /* `display` MUST come from classes, not the inline style. An inline
         `display: grid` beats any utility, so `lg:hidden` never applied and
         the sidebar stayed on screen between lg and xl — where the body has
         no padding, so it sat on top of the page content. Tailwind emits
         responsive variants after base utilities, so grid → lg:hidden →
         xl:grid resolves in that order. */
      isAsideLgActive ? 'grid' : 'grid lg:hidden xl:grid'
    ]"
    aria-label="Main navigation"
    :inert="isOffCanvasClosed"
    :aria-hidden="isOffCanvasClosed ? 'true' : null"
  >
    <!-- ── Brand ── -->
    <div
      class="flex w-full h-14 items-center gap-2 border-b border-gray-200 dark:border-gray-700"
      :class="isRail ? 'px-2 justify-center' : 'px-4'"
    >
      <!-- Visible at every width the sidebar is an overlay at, not just lg–xl:
           below lg the only other candidate is NavBar's back-burger, which the
           overlay paints over (App.vue stacks it one step above the navbar), so
           an open drawer had no close affordance at all on a phone.
           `min-h/min-w-[44px]` because NavBarItem's own `p-2` around a 24px icon
           lands at 40px — under the touch minimum for the drawer's exit. -->
      <nav-bar-item
        type="flex xl:hidden min-w-[44px] min-h-[44px]"
        aria-label="Close navigation menu"
        @click="onCloseButton"
      >
        <icon :path="mdiClose" class="cursor-pointer" :size="24" />
      </nav-bar-item>

      <router-link
        to="/dashboard"
        class="flex items-center min-w-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        :class="isRail ? 'justify-center' : 'flex-1'"
        aria-label="Toplistbot dashboard"
      >
        <jb-logo height="h-8" text-size="text-lg" :show-text="!isRail" />
      </router-link>

      <button
        v-if="!isRail"
        type="button"
        aria-label="Collapse sidebar"
        title="Collapse sidebar"
        class="hidden xl:flex items-center justify-center w-8 h-8 rounded-lg flex-none transition-colors
               text-gray-400 hover:text-gray-700 hover:bg-gray-100
               dark:hover:text-gray-200 dark:hover:bg-gray-700
               focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        @click="toggleCollapse"
      >
        <icon :path="mdiChevronLeft" class="flex-none" w="w-4" h="h-4" :size="18" />
      </button>
    </div>

    <!-- ── Navigation ── -->
    <nav
      class="py-3 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent dark:scrollbar-thumb-gray-600"
      style="min-height: 0; -webkit-overflow-scrolling: touch"
    >
      <template v-for="(menuGroup, index) in menu">
        <!-- Tier boundary. menu.js exports this sentinel as `DIVIDER`; it is a
             rule at both widths, where a heading is text at one and a rule at
             the other. Checked before the heading branch, since it is also a
             string. -->
        <hr
          v-if="menuGroup === DIVIDER"
          :key="`d-${index}`"
          class="mx-3 my-2 border-t border-gray-200 dark:border-gray-700"
        >
        <p
          v-else-if="typeof menuGroup === 'string' && !isRail"
          :key="`a-${index}`"
          class="px-4 pt-4 pb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 first:pt-1"
        >
          {{ menuGroup }}
        </p>
        <!-- Collapsed to icon width the labels have nowhere to go, so the
             grouping is carried by a rule instead. -->
        <hr
          v-else-if="typeof menuGroup === 'string'"
          :key="`c-${index}`"
          class="mx-3 my-2 border-t border-gray-200 dark:border-gray-700"
        >
        <aside-menu-list
          v-else-if="Array.isArray(menuGroup)"
          :key="`b-${index}`"
          :menu="menuGroup"
          :is-collapsed="isRail"
          @menu-click="menuClick"
        />
      </template>
    </nav>

    <!-- ── Footer ──
         `min-w-0` is load-bearing, not tidiness. This is a GRID item (the aside
         sets gridTemplateRows), and a grid item's default `min-width: auto`
         refuses to shrink below its content. A long email therefore made this
         track wider than the 15rem sidebar; the aside's `overflow: hidden` then
         cut the text off mid-character instead of the `truncate` inside ever
         producing an ellipsis — the text element was never the constrained one. -->
    <div
      v-if="isLoggedIn"
      class="min-w-0 border-t border-gray-200 dark:border-gray-700"
      :class="isRail ? 'px-2 py-2 space-y-2' : 'px-3 py-3 space-y-3'"
    >
      <!-- Tokens. Stays quiet when funded; a low balance earns amber AND the
           words "Top up", so the colour is never the only thing saying it.
           Same rule as the top bar (composables/useTokenBalance.js). -->
      <router-link
        to="/products"
        class="group flex items-center rounded-lg transition-colors
               focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        :class="[
          isRail ? 'flex-col justify-center px-1 py-1.5' : 'gap-2 px-2.5 py-2',
          !isLowBalance
            ? 'bg-gray-50 hover:bg-gray-100 dark:bg-gray-900 dark:hover:bg-gray-700'
            : 'bg-amber-50 hover:bg-amber-100 dark:bg-amber-900 dark:bg-opacity-20 dark:hover:bg-opacity-30'
        ]"
        :title="tokensLabel"
        :aria-label="isRail ? tokensLabel : null"
      >
        <template v-if="!isRail">
          <icon
            :path="mdiWalletOutline"
            class="flex-none"
            :class="!isLowBalance ? 'text-gray-400 dark:text-gray-500' : 'text-amber-500'"
            w="w-4"
            h="h-4"
            :size="16"
          />
          <span class="flex-1 min-w-0 text-xs text-gray-500 dark:text-gray-400">Tokens</span>
          <span
            class="flex-none text-sm font-semibold tabular-nums"
            :class="!isLowBalance ? 'text-gray-900 dark:text-gray-100' : 'text-amber-700 dark:text-amber-300'"
          >
            {{ formattedTokens }}
          </span>
          <span
            v-if="isLowBalance"
            class="flex-none text-xs font-semibold text-amber-800 dark:text-amber-200"
          >
            Top up
          </span>
          <icon
            v-else
            :path="mdiPlus"
            class="flex-none text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 dark:text-gray-500"
            w="w-4"
            h="h-4"
            :size="16"
          />
        </template>
        <template v-else>
          <span
            class="text-3xs font-semibold leading-tight tabular-nums"
            :class="!isLowBalance ? 'text-gray-600 dark:text-gray-300' : 'text-amber-700 dark:text-amber-300'"
          >
            {{ shortTokens }}
          </span>
          <span
            v-if="isLowBalance"
            class="text-3xs font-semibold leading-tight text-amber-800 dark:text-amber-200"
          >
            Top up
          </span>
        </template>
      </router-link>

      <!-- Account -->
      <div
        class="flex min-w-0 items-center"
        :class="isRail ? 'justify-center' : 'gap-2.5 px-1'"
        :title="isRail ? (userName || 'User') : userEmail || null"
      >
        <user-avatar :username="userName" class="w-8 h-8 flex-none" />
        <!-- Name only.
             The address used to sit under it and earned almost nothing: at
             15rem a long one truncated to "henk.renderer374@passm…", which
             identifies nobody, while costing a permanent second line in a
             footer already carrying credits, four utility buttons and a rule.
             The name above it already answers "which account am I in".
             It is not lost — the navbar account dropdown renders it in full
             (NavBar.vue), the Account page shows it editable, and it is still
             on this row's `title` for a hover. -->
        <div v-if="!isRail" class="min-w-0 flex-1">
          <div class="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
            {{ userName || 'User' }}
          </div>
        </div>
      </div>

      <!-- Utilities.
           Three columns (dark mode, guide, chat), not four: four cells across
           the 15rem drawer are ~51px wide and ~40px tall, under the 44px touch
           target on the one surface that is only ever driven by thumb.
           `grid-cols-4` is kept from xl, where these are mouse targets in a
           permanently docked panel. -->
      <div
        class="border-t border-gray-200 dark:border-gray-700"
        :class="isRail ? 'pt-2 space-y-1' : 'pt-2 grid grid-cols-3 gap-2 xl:gap-1'"
      >
        <component
          :is="util.href ? 'a' : 'button'"
          v-for="util in utilities"
          :key="util.key"
          :type="util.href ? null : 'button'"
          :href="util.href || null"
          :target="util.href ? '_blank' : null"
          :rel="util.href ? 'noopener noreferrer' : null"
          :referrerpolicy="util.href ? 'no-referrer' : null"
          :aria-label="util.title"
          :title="util.title"
          class="flex items-center justify-center rounded-lg transition-colors cursor-pointer
                 focus:outline-none focus-visible:ring-2"
          :class="[
            isRail ? 'w-full py-2' : 'flex-col gap-0.5 py-2.5 xl:py-1.5',
            util.danger
              ? 'text-gray-500 hover:bg-red-50 hover:text-red-600 focus-visible:ring-red-500 dark:text-gray-400 dark:hover:bg-red-900 dark:hover:bg-opacity-20 dark:hover:text-red-400'
              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-brand-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100'
          ]"
          @click="util.action && util.action()"
        >
          <icon
            :path="util.icon"
            class="flex-none"
            :w="isRail ? 'w-5' : 'w-4'"
            :h="isRail ? 'h-5' : 'h-4'"
            :size="isRail ? 18 : 16"
          />
          <span v-if="!isRail" class="text-3xs font-medium leading-none">
            {{ util.label }}
          </span>
        </component>

        <button
          v-if="isRail"
          type="button"
          aria-label="Expand sidebar"
          title="Expand sidebar"
          class="hidden xl:flex items-center justify-center w-full py-2 rounded-lg transition-colors
                 text-gray-500 hover:bg-gray-100 hover:text-gray-900
                 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          @click="toggleCollapse"
        >
          <icon :path="mdiChevronRight" class="flex-none" w="w-5" h="h-5" :size="18" />
        </button>
      </div>
    </div>
  </aside>
</template>
